import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useSwipeable, useIntersectionObserver } from '../../../../utils';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './AdvancedPDFViewer.css';

// Simple animation utilities
const motion = {
  div: ({ children, style, className, initial, animate, exit, transition, ...props }) => (
    <div
      className={`${className || ''} motion-element`}
      style={{ ...style, transition: 'all 0.3s ease' }}
      {...props}
    >
      {children}
    </div>
  )
};


// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const AdvancedPDFViewer = ({
  pdfUrl,
  isMobile,
  onProgress,
  onSignatureFieldDetected,
  onSignatureClick,
  onPageChange,
  initialScale = 1.0
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(isMobile ? 1.0 : initialScale);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbnails, setThumbnails] = useState([]);
  const [signatureFields, setSignatureFields] = useState([]);
  const [viewMode] = useState(isMobile ? 'single' : 'continuous');
  const [pdfDocument, setPdfDocument] = useState(null);
  const [preloadedPages, setPreloadedPages] = useState(new Set());
  const [isZoomed, setIsZoomed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const containerRef = useRef();
  const pagesRefs = useRef({});

  // Effect to handle URL changes - moved to top to avoid hooks rule violation
  useEffect(() => {
    if (pdfUrl) {
      setLoading(true);
      setError(null);
      setPdfDocument(null);
      setNumPages(null);
      setCurrentPage(1);
    }
  }, [pdfUrl]);

  // Intersection observer for lazy loading and progress tracking
  const { ref: observerRef } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (inView, entry) => {
      if (inView && entry.target.dataset.pageNumber) {
        const pageNum = parseInt(entry.target.dataset.pageNumber);
        setCurrentPage(pageNum);
        onPageChange?.(pageNum);
        
        // Calculate progress
        const progress = (pageNum / numPages) * 100;
        onProgress?.(progress);
      }
    }
  });

  // Generate thumbnails for navigation
  const generateThumbnails = useCallback(async (pdf) => {
    const thumbs = [];
    for (let i = 1; i <= Math.min(pdf.numPages, 20); i++) {
      try {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.2 });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
        
        thumbs.push({
          pageNumber: i,
          thumbnail: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height
        });
      } catch (err) {
        console.warn(`Failed to generate thumbnail for page ${i}:`, err);
      }
    }
    setThumbnails(thumbs);
  }, []);

  // Intelligent signature field detection
  const detectSignatureFields = useCallback(async (pdf) => {
    const fields = [];
    const signatureKeywords = [
      'atas nama klien',
    ];

    for (let i = 1; i <= pdf.numPages; i++) {
      try {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        textContent.items.forEach((item) => {
          const text = item.str.toLowerCase();
          const isSignatureField = signatureKeywords.some(keyword => 
            text.includes(keyword)
          );
          
          if (isSignatureField) {
            fields.push({
              pageNumber: i,
              x: item.transform[4],
              y: item.transform[5],
              width: item.width || 100,
              height: item.height || 30,
              text: item.str,
              confidence: calculateFieldConfidence(text, signatureKeywords)
            });
          }
        });
      } catch (err) {
        console.warn(`Failed to detect fields on page ${i}:`, err);
      }
    }
    
    setSignatureFields(fields);
    onSignatureFieldDetected?.(fields);
  }, [onSignatureFieldDetected]);

  const calculateFieldConfidence = (text, keywords) => {
    const matches = keywords.filter(keyword => text.includes(keyword)).length;
    return Math.min(matches / keywords.length, 1) * 100;
  };

  // Page preloading for smooth navigation
  const preloadPages = useCallback(async (pdf, start, end) => {
    const newPreloaded = new Set(preloadedPages);
    
    for (let i = start; i <= end; i++) {
      if (!newPreloaded.has(i)) {
        try {
          await pdf.getPage(i);
          newPreloaded.add(i);
        } catch (err) {
          console.warn(`Failed to preload page ${i}:`, err);
        }
      }
    }
    
    setPreloadedPages(newPreloaded);
  }, [preloadedPages]);

  // PDF Document Loading
  const onDocumentLoadSuccess = useCallback((pdf) => {
    setNumPages(pdf.numPages);
    setPdfDocument(pdf);
    setLoading(false);
    setError(null); // Clear any previous errors
    
    // Generate thumbnails
    generateThumbnails(pdf);
    
    // Detect signature fields
    detectSignatureFields(pdf);
    
    // Preload first few pages
    preloadPages(pdf, 1, Math.min(3, pdf.numPages));
    
    // Notify parent component
    onProgress?.(0); // Reset progress
  }, [onProgress, detectSignatureFields, preloadPages, generateThumbnails]);


  // Navigation functions
  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= numPages) {
      setCurrentPage(pageNum);

      if (pagesRefs.current[pageNum]) {
        pagesRefs.current[pageNum].scrollIntoView({
          behavior: 'smooth',
          block: isMobile ? 'center' : 'start'
        });
      }

      // Preload adjacent pages
      const preloadStart = Math.max(1, pageNum - 1);
      const preloadEnd = Math.min(numPages, pageNum + 1);
      preloadPages(pdfDocument, preloadStart, preloadEnd);
    }
  };

  const nextPage = () => goToPage(currentPage + 1);
  const prevPage = () => goToPage(currentPage - 1);

  // Zoom functions
  const zoomIn = () => {
    setScale(prev => {
      const newScale = Math.min(prev + 0.1, 3.0);
      setIsZoomed(newScale > 1.0);
      return newScale;
    });
  };

  const zoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.1, 0.3);
      setIsZoomed(newScale > 1.0);
      return newScale;
    });
  };

  const resetZoom = () => {
    setScale(isMobile ? 0.7 : 1.0);
    setIsZoomed(false);
    setIsPanning(false);
  };
  const fitToWidth = () => {
    if (containerRef.current && pdfDocument) {
      const containerWidth = containerRef.current.clientWidth - 40;
      // Get the actual page width from the PDF document
      pdfDocument.getPage(1).then(page => {
        const viewport = page.getViewport({ scale: 1.0 });
        const pageWidth = viewport.width;
        const optimalScale = Math.min(containerWidth / pageWidth, 1.2); // Cap at 120% for readability
        const newScale = Math.max(optimalScale, 0.5); // Minimum 50% scale
        setScale(newScale);
        setIsZoomed(newScale > 1.0);
      }).catch(err => {
        console.warn('Failed to get page dimensions for fitToWidth:', err);
        // Fallback to estimated calculation
        const fallbackScale = Math.min(containerWidth / 595, 1.0);
        setScale(fallbackScale);
        setIsZoomed(fallbackScale > 1.0);
      });
    }
  };

  // Auto-fit on container resize
  const handleResize = useCallback(() => {
    if (containerRef.current && !isMobile) {
      const containerWidth = containerRef.current.clientWidth - 40;
      if (containerWidth > 0) {
        fitToWidth();
      }
    }
  }, [isMobile, pdfDocument, fitToWidth]);

  // Effect to handle window resize
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Mobile swipe handlers - intelligent navigation based on zoom state
  const swipeHandlers = useSwipeable({
    onSwipedLeft: (eventData) => {
      // When zoomed, only allow page navigation for very intentional swipes
      if (!isMobile) return;

      const minDistance = isZoomed ? 100 : 40; // Much more restrictive when zoomed
      const isHorizontalSwipe = Math.abs(eventData.deltaX) > minDistance &&
                               Math.abs(eventData.deltaY) < Math.abs(eventData.deltaX) * 0.2;

      if (isHorizontalSwipe && eventData.velocity > 0.5 && !isPanning) {
        nextPage();
      }
    },
    onSwipedRight: (eventData) => {
      // When zoomed, only allow page navigation for very intentional swipes
      if (!isMobile) return;

      const minDistance = isZoomed ? 100 : 40; // Much more restrictive when zoomed
      const isHorizontalSwipe = Math.abs(eventData.deltaX) > minDistance &&
                               Math.abs(eventData.deltaY) < Math.abs(eventData.deltaX) * 0.2;

      if (isHorizontalSwipe && eventData.velocity > 0.5 && !isPanning) {
        prevPage();
      }
    },
    onSwiping: (eventData) => {
      // Track panning state for zoomed content
      if (isZoomed && (Math.abs(eventData.deltaX) > 10 || Math.abs(eventData.deltaY) > 10)) {
        setIsPanning(true);
      }
    },
    onSwipeEnd: () => {
      // Reset panning state after swipe ends
      setTimeout(() => setIsPanning(false), 300);
    },
    preventDefaultTouchmoveEvent: false, // Allow normal scrolling
    trackMouse: false,
    trackTouch: true,
    delta: { x: 8, y: 8 }, // Very low threshold for responsiveness
    swipeDuration: 500, // Longer duration to distinguish from scrolling
    touchEventOptions: { passive: true } // Better performance
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't interfere with text input in form fields
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true' ||
        activeElement.getAttribute('role') === 'textbox'
      );

      // Skip navigation if user is typing in an input field
      if (isInputField) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          prevPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault();
          nextPage();
          break;
        case 'Home':
          e.preventDefault();
          goToPage(1);
          break;
        case 'End':
          e.preventDefault();
          goToPage(numPages);
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        default:
          // No action for other keys
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, numPages, goToPage, nextPage, prevPage, resetZoom]);

  if (error) {
    return (
      <div className="pdf-error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={() => window.location.reload()}>
            Retry with Advanced Viewer
          </button>
          <div className="fallback-viewer">
            <p>Fallback to simple viewer:</p>
            <iframe
              src={pdfUrl}
              title="PDF Document"
              width="100%"
              height="600"
              style={{ border: 'none', borderRadius: '8px' }}
            />
          </div>
        </div>
      </div>
    );
  }

  // If no PDF URL yet, show loading
  if (!pdfUrl) {
    return (
      <div className="pdf-loading">
        <div className="loading-spinner"></div>
        <p>Preparing document...</p>
      </div>
    );
  }

  return (
    <div className={`advanced-pdf-viewer ${isMobile ? 'mobile' : ''}`} ref={containerRef}>
      {/* Loading overlay */}
      {loading && (
        <div className="pdf-loading">
          <div className="loading-spinner"></div>
          <p>Loading document...</p>
        </div>
      )}
      {/* Toolbar */}
      {isMobile ? (
        <div className="pdf-toolbar-mobile">
          <div className="mobile-toolbar-content">
            <button onClick={prevPage} disabled={currentPage <= 1} className="mobile-btn">
              ←
            </button>
            <span className="mobile-page-info">
              {currentPage} / {numPages}
            </span>
            <button onClick={nextPage} disabled={currentPage >= numPages} className="mobile-btn">
              →
            </button>
            <div className="mobile-zoom">
              <button onClick={zoomOut} className="mobile-btn">−</button>
              <span className="mobile-zoom-level">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} className="mobile-btn">+</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="pdf-toolbar">
          <div className="toolbar-section">
            <div className="page-controls">
              <button onClick={prevPage} disabled={currentPage <= 1}>
                ←
              </button>
              <span className="page-info">
                <input
                  type="number"
                  value={currentPage}
                  onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                  min="1"
                  max={numPages}
                  className="page-input"
                />
                <span>of {numPages}</span>
              </span>
              <button onClick={nextPage} disabled={currentPage >= numPages}>
                →
              </button>
            </div>
          </div>

          <div className="toolbar-section">
            <div className="zoom-controls">
              <button onClick={zoomOut} title="Zoom Out">−</button>
              <span className="zoom-level">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} title="Zoom In">+</button>
              <button onClick={fitToWidth} title="Fit Width">⟷</button>
              <button onClick={resetZoom} title="Reset Zoom">⌂</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Content */}
      <div className={`pdf-content ${viewMode}`} {...swipeHandlers}>
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div className="page-loading">Loading page...</div>}
        >
          {viewMode === 'single' ? (
            // Single page view (mobile)
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: isMobile ? 100 : 0 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isMobile ? -100 : 0 }}
              transition={{ duration: 0.3 }}
              className="page-container"
              ref={(el) => pagesRefs.current[currentPage] = el}
              data-page-number={currentPage}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                className="pdf-page"
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </motion.div>
          ) : (
            // Continuous view (desktop)
            <div className="pages-container">
              {Array.from(new Array(numPages), (el, index) => {
                const pageNum = index + 1;
                return (
                  <div
                    key={pageNum}
                    className="page-wrapper"
                    ref={(el) => {
                      pagesRefs.current[pageNum] = el;
                      if (pageNum === currentPage) observerRef.current = el;
                    }}
                    data-page-number={pageNum}
                  >
                    <Page
                      pageNumber={pageNum}
                      scale={scale}
                      className="pdf-page"
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      loading={<div className="page-loading">Loading page {pageNum}...</div>}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Document>

      </div>

      {/* Thumbnail navigation sidebar */}
      {!isMobile && thumbnails.length > 0 && (
        <div className="thumbnails-sidebar">
          <h4>Pages</h4>
          <div className="thumbnails-grid">
            {thumbnails.map((thumb) => (
              <div
                key={thumb.pageNumber}
                className={`thumbnail ${currentPage === thumb.pageNumber ? 'active' : ''}`}
                onClick={() => goToPage(thumb.pageNumber)}
              >
                <img src={thumb.thumbnail} alt={`Page ${thumb.pageNumber}`} />
                <span>{thumb.pageNumber}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating Action Button for Signature */}
      {/* <button
        className="floating-sign-btn"
        onClick={onSignatureClick}
        aria-label="Create signature"
      >
        ✍️
      </button> */}
    </div>
  );
};

export default AdvancedPDFViewer;